import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Image,
  Platform,
  useWindowDimensions,
  type StyleProp,
  type ImageStyle,
  type ViewStyle,
  type GestureResponderEvent,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { text } from '@vuelto/tokens';

type ZoomableImageProps = {
  uri: string;
  style?: StyleProp<ImageStyle>;
  /** Modal header label (default: Imagen). */
  title?: string;
  /** When provided, internal elements get testID={`${testID}.view`}, `${testID}.zoom-in`, etc. */
  testID?: string;
  /** El trigger ocupa todo el contenedor padre (sin él mide por el tamaño de la imagen). */
  fill?: boolean;
  /** resizeMode del preview (default: contain). 'cover' llena el bloque recortando. */
  thumbResizeMode?: 'cover' | 'contain' | 'center';
};

/** Near-black scrim for photo reading — darker than `surface.overlay`. */
const LIGHTBOX_SCRIM = 'rgba(12, 10, 9, 0.94)';
const TOOL_BG = 'rgba(255, 255, 255, 0.16)';
const PAD = 16;
const DESKTOP_MAX_W = 960;

export function ZoomableImage({ uri, style, title = 'Imagen', testID, fill = false, thumbResizeMode = 'contain' }: ZoomableImageProps) {
  const [modalVisible, setModalVisible] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(
    null,
  );

  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const openModal = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setModalVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(Number((z + 0.5).toFixed(1)), 5));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const next = Math.max(Number((z - 0.5).toFixed(1)), 1);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (!modalVisible || Platform.OS !== 'web') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
      if (e.key === '0') resetZoom();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalVisible, closeModal, zoomIn, zoomOut, resetZoom]);

  // Web wheel zoom — portal parity. ponytail: DOM listener only on web.
  useEffect(() => {
    if (!modalVisible || Platform.OS !== 'web') return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        setZoom((z) => Math.min(Number((z + 0.3).toFixed(1)), 5));
      } else {
        setZoom((z) => {
          const next = Math.max(Number((z - 0.3).toFixed(1)), 1);
          if (next === 1) setPan({ x: 0, y: 0 });
          return next;
        });
      }
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [modalVisible]);

  const headerTop = Math.max(insets.top, Platform.OS === 'ios' ? 12 : 8) + 4;
  const headerBottom = 8;
  const headerBlock = headerTop + 44 + headerBottom;
  const maxImgW = Math.min(winW - PAD * 2, Platform.OS === 'web' ? DESKTOP_MAX_W : winW - PAD * 2);
  const maxImgH = Math.max(200, winH - headerBlock - insets.bottom - PAD * 2);
  const zoomed = zoom > 1;

  const onPanGrant = useCallback(
    (e: GestureResponderEvent) => {
      if (zoom <= 1) return;
      const { pageX, pageY } = e.nativeEvent;
      dragRef.current = {
        startX: pageX,
        startY: pageY,
        baseX: pan.x,
        baseY: pan.y,
      };
    },
    [zoom, pan.x, pan.y],
  );

  const onPanMove = useCallback((e: GestureResponderEvent) => {
    if (!dragRef.current) return;
    const { pageX, pageY } = e.nativeEvent;
    setPan({
      x: dragRef.current.baseX + (pageX - dragRef.current.startX),
      y: dragRef.current.baseY + (pageY - dragRef.current.startY),
    });
  }, []);

  const onPanEnd = useCallback(() => {
    dragRef.current = null;
  }, []);

  const thumbStyle: StyleProp<ViewStyle> = [
    fill ? ({ width: '100%', height: '100%' } as ViewStyle) : undefined,
    Platform.OS === 'web' ? ({ cursor: 'zoom-in' } as unknown as ViewStyle) : undefined,
  ];

  const imageTransformStyle = {
    width: maxImgW,
    height: maxImgH,
    transform: [{ translateX: pan.x }, { translateY: pan.y }, { scale: zoom }],
    // RN-web: transformOrigin is valid CSS but not in ImageStyle typings.
    ...(Platform.OS === 'web' ? ({ transformOrigin: 'center center' } as object) : null),
  } as StyleProp<ImageStyle>;

  return (
    <>
      <Pressable
        onPress={openModal}
        accessibilityRole="button"
        accessibilityLabel={`Ver ${title}`}
        testID={testID ? `${testID}.view` : undefined}
        style={({ pressed }) => [thumbStyle, pressed ? { opacity: 0.85 } : null]}
      >
        <Image source={{ uri }} style={style ?? styles.defaultImage} resizeMode={thumbResizeMode} />
      </Pressable>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={closeModal}
        statusBarTranslucent
      >
        <View style={styles.modalRoot}>
          {/* Backdrop: closes only when not zoomed so pans aren't stolen. */}
          {!zoomed ? (
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={closeModal}
              accessibilityLabel="Cerrar imagen"
              accessibilityRole="button"
            />
          ) : null}

          <View
            style={[styles.modalHeader, { paddingTop: headerTop, paddingBottom: headerBottom }]}
            pointerEvents="box-none"
          >
            <Text style={styles.modalTitle} numberOfLines={1}>
              {title}
            </Text>
            <View style={styles.toolbar}>
              <Pressable
                onPress={zoomOut}
                disabled={zoom <= 1}
                style={[styles.toolBtn, zoom <= 1 && styles.toolBtnDisabled]}
                accessibilityLabel="Alejar"
                accessibilityRole="button"
                testID={testID ? `${testID}.zoom-out` : undefined}
              >
                <Text style={styles.toolBtnText}>−</Text>
              </Pressable>
              <Text style={styles.zoomLabel}>{zoom.toFixed(1)}×</Text>
              <Pressable
                onPress={zoomIn}
                disabled={zoom >= 5}
                style={[styles.toolBtn, zoom >= 5 && styles.toolBtnDisabled]}
                accessibilityLabel="Acercar"
                accessibilityRole="button"
                testID={testID ? `${testID}.zoom-in` : undefined}
              >
                <Text style={styles.toolBtnText}>+</Text>
              </Pressable>
              {zoomed ? (
                <Pressable
                  onPress={resetZoom}
                  style={styles.toolBtnWide}
                  accessibilityLabel="Restablecer zoom"
                  accessibilityRole="button"
                  testID={testID ? `${testID}.zoom-reset` : undefined}
                >
                  <Text style={styles.toolBtnTextSm}>1×</Text>
                </Pressable>
              ) : null}
              <Pressable
                onPress={closeModal}
                style={styles.closeBtn}
                accessibilityLabel="Cerrar"
                accessibilityRole="button"
                testID={testID ? `${testID}.close` : undefined}
              >
                <Text style={styles.closeBtnText}>Cerrar</Text>
              </Pressable>
            </View>
          </View>

          {/*
            Stage clips overflow. Image keeps fixed layout size; zoom/pan via transform
            so the lightbox never grows past the viewport (esp. RN-web).
          */}
          <View
            style={[
              styles.stage,
              Platform.OS === 'web' && zoomed
                ? ({ cursor: 'grab' } as unknown as ViewStyle)
                : null,
            ]}
            onStartShouldSetResponder={() => zoomed}
            onMoveShouldSetResponder={() => zoomed}
            onResponderGrant={onPanGrant}
            onResponderMove={onPanMove}
            onResponderRelease={onPanEnd}
            onResponderTerminate={onPanEnd}
            // At 1×, let backdrop Pressable receive taps outside the image.
            pointerEvents={zoomed ? 'auto' : 'box-none'}
          >
            <Image
              source={{ uri }}
              style={imageTransformStyle}
              resizeMode="contain"
              accessibilityLabel={title}
            />
          </View>

          {Platform.OS === 'web' && !zoomed ? (
            <Text style={styles.hint} pointerEvents="none">
              Scroll o +/− · Esc o clic fuera
            </Text>
          ) : null}
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  defaultImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    marginTop: 8,
  },
  modalRoot: {
    flex: 1,
    backgroundColor: LIGHTBOX_SCRIM,
  },
  modalHeader: {
    zIndex: 2,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 8,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  modalTitle: {
    flexShrink: 1,
    fontSize: 15,
    fontWeight: '700',
    color: text.onBrand,
    maxWidth: '28%',
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    gap: 6,
  },
  toolBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: TOOL_BG,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toolBtnDisabled: {
    opacity: 0.4,
  },
  toolBtnWide: {
    paddingHorizontal: 12,
    height: 40,
    borderRadius: 10,
    backgroundColor: TOOL_BG,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toolBtnText: {
    fontSize: 20,
    fontWeight: '700',
    color: text.onBrand,
    lineHeight: 22,
  },
  toolBtnTextSm: {
    fontSize: 13,
    fontWeight: '700',
    color: text.onBrand,
  },
  closeBtn: {
    paddingHorizontal: 14,
    height: 40,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.28)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.35)',
  },
  closeBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: text.onBrand,
  },
  zoomLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: text.onBrand,
    minWidth: 40,
    textAlign: 'center',
  },
  stage: {
    flex: 1,
    zIndex: 1,
    width: '100%',
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
  hint: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
    color: 'rgba(255,255,255,0.45)',
    zIndex: 2,
  },
});
